import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { buildUrl, isDefined } from '@trimble-gcs/common';
import { catchError, filter, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { ConnectProject } from 'trimble-connect-workspace-api';
import { AppState } from '../app-state/app.state';
import { AuthState } from '../auth/auth.state';
import { ClientHeaderService } from '../client-header/client-header.service';
import { HANDLE_ERROR } from '../error-handling/error-handler-operator';
import { injectLogger } from '../logging/logger-injection';
import { GET_SCAN_PROJECT_URL } from '../utils/get-scan-project-url';
import { isRegionNotFoundError, RegionNotFoundError } from './region-not-found.error';
import { GlobalRegion, Project, ProjectUpdate } from './tenancy.models';

@Injectable({
  providedIn: 'root',
})
export class TenancyService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly clientHeaderService = inject(ClientHeaderService);
  private readonly logger = injectLogger('TenancyService');
  private readonly getScanProjectUrl$ = inject(GET_SCAN_PROJECT_URL);
  private readonly handleError = inject(HANDLE_ERROR);

  getProject(): Observable<Project> {
    return this.getScanProjectUrl$('').pipe(
      switchMap((url) => this.http.get<Project>(url)),
      this.handleError('projectLoadError'),
    );
  }

  updateProject(projectUpdate: Partial<ProjectUpdate>): Observable<Project> {
    return this.getScanProjectUrl$('').pipe(
      switchMap((url) => this.http.patch<Project>(url, projectUpdate)),
      this.handleError('projectSaveError'),
    );
  }

  createTenancy(): Observable<Project> {
    return this.getConnectProjectAndGlobalRegion().pipe(
      switchMap(({ connectProject, globalRegion }) => {
        const url = buildUrl(globalRegion.sdsBaseUrl, `/projects`);
        const token = this.store.selectSnapshot(AuthState.accessToken);
        const project = {
          projectId: connectProject.id,
          projectLocation: connectProject.location,
        };

        return this.http
          .post<Project>(url, project, {
            headers: {
              Authorization: `Bearer ${token}`,
              ...this.clientHeaderService.getClientHeaders(),
            },
          })
          .pipe(
            catchError((err) => {
              this.logger.error(`Create tenancy for project ${connectProject.id} failed`, {}, err);
              throw err;
            }),
          );
      }),
    );
  }

  getGlobalRegion(connectProject: ConnectProject): Observable<GlobalRegion> {
    const regionsUrl = this.store.selectSnapshot(AppState.settings).endpoints.regions.url;

    return this.http.get<GlobalRegion[]>(regionsUrl).pipe(
      map((regions) => regions.find((x) => x.connectRegion === connectProject.location)),
      filter((region): region is GlobalRegion => {
        if (!isDefined(region)) {
          throw new RegionNotFoundError(connectProject.location ?? 'unknown');
        }

        return true;
      }),
    );
  }

  isProjectRegionSupported(): Observable<boolean> {
    return this.store.selectOnce(AppState.project).pipe(
      filter(isDefined),
      switchMap((connectProject) => this.getGlobalRegion(connectProject)),
      map(() => true),
      catchError((err) => {
        return isRegionNotFoundError(err) ? of(false) : throwError(() => err);
      }),
    );
  }

  private getConnectProjectAndGlobalRegion(): Observable<{
    connectProject: ConnectProject;
    globalRegion: GlobalRegion;
  }> {
    return this.store.select(AppState.project).pipe(
      filter(isDefined),
      take(1),
      switchMap((connectProject) => {
        return this.getGlobalRegion(connectProject).pipe(
          map((globalRegion) => ({ connectProject, globalRegion })),
        );
      }),
    );
  }
}
